import { inject, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, setAllEntities, setEntity, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { BlocCompétences } from '../../croisement-référentiel/models/bloc-compétences';
import { Compétence } from '../../croisement-référentiel/models/compétences';
import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { BlocCompétencesService } from '../bloc-compétences.service';
import { configBlocCompétences } from './bloc-compétences.store';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';

export function avecMéthodesBlocCompétences<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<BlocCompétences> & SelectedEntityState>(),
      props: type<EntityProps<BlocCompétences> & { selectedEntity: Signal<BlocCompétences | null> }>(),
    },
    withMethods((store) => ({
      chargerBlocs: (state: { blocs: BlocCompétences[] }) =>
        patchState(store, setAllEntities(state.blocs, configBlocCompétences)),
      ajouterBloc: (state: { bloc: BlocCompétences }) =>
        patchState(store, setEntity(state.bloc, configBlocCompétences)),
      modifierBloc: (state: { bloc: BlocCompétences }) =>
        patchState(store, updateEntity({ id: state.bloc.codeBloc, changes: state.bloc }, configBlocCompétences)),
      selectionerBloc: (state: { codeBloc: string }) => patchState(store, { selectedEntityId: state.codeBloc }),
    })),
    withMethods((store, service = inject(BlocCompétencesService)) => ({
      modifier: rxMethod<{ bloc: BlocCompétences; idReferentiel: string; next: () => void }>(
        pipe(
          tap(({ bloc }) => store.modifierBloc({ bloc })),
          switchMap(({ bloc, idReferentiel, next }) =>
            service.modifier(bloc, idReferentiel).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification du bloc de compétences, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      ajouterCompétence: (comp: Compétence) => {
        const bloc = store.selectedEntity();
        if (!bloc) return;
        bloc.competences.push(comp);
        store.modifierBloc({ bloc });
      },
      modifierCompétence: (comp: Compétence) => {
        const bloc = store.selectedEntity();
        if (!bloc) return;
        const idx = bloc.competences.findIndex((x) => x.id === comp.id);
        if (idx !== -1) {
          bloc.competences[idx] = comp;
          store.modifierBloc({ bloc });
        }
      },
      supprimerCompétence: (comp: Compétence) => {
        const bloc = store.selectedEntity();
        if (!bloc) return;
        const idx = bloc.competences.findIndex((x) => x.id === comp.id);
        if (idx !== -1) {
          bloc.competences.splice(idx, 1);
          store.modifierBloc({ bloc });
        }
      },
    })),
  );
}
